import React, { useState } from "react";
import { fs } from "../firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import {
    GoogleAuthProvider,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    getAuth,
    setPersistence,
    browserLocalPersistence,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { hasCompletedOnboarding, saveUserSession, getUserPhoneNumber } from "../utils/userUtils";
import "../css/AuthModal.css";

function AuthModal({ isOpen, onClose, onAuthSuccess }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const auth = getAuth();
    const googleProvider = new GoogleAuthProvider();

    const [authMode, setAuthMode] = useState("register");
    const [mail, setMail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleGoogleLogin = async () => {
        setError("");
        setLoading(true);
        try {
            await setPersistence(auth, browserLocalPersistence);
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;
            await handleAuthResult(user);
        } catch (err) {
            console.error("Google login error:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAuthResult = async (user) => {
        const userDocRef = doc(fs, "users", user.uid);
        const docSnap = await getDoc(userDocRef);

        if (docSnap.exists()) {
            const userData = docSnap.data();

            if (!hasCompletedOnboarding(userData)) {
                localStorage.setItem("isNewUser", "true");
                navigate("/onboarding");
                return;
            }

            const redirectPath = localStorage.getItem("redirectAfterLogin");
            if (redirectPath) {
                localStorage.removeItem("redirectAfterLogin");
                navigate(redirectPath);
                return;
            }

            localStorage.removeItem("isNewUser");

            const sessionData = saveUserSession(userData, user);

            if (onAuthSuccess) {
                onAuthSuccess({
                    ...sessionData,
                    cardNumber: userData.CardNumber || userData.cardNumber || "",
                    phoneNumber: getUserPhoneNumber(userData) || "",
                });
            }
        } else {
            localStorage.setItem("isNewUser", "true");
            await setDoc(userDocRef, {
                name: user.displayName || "",
                mail: user.email || "",
                uid: user.uid,
                isNewUser: true,
                onboardingCompleted: false,
                createdAt: new Date().toISOString(),
            });
            navigate("/onboarding");
        }
    };

    const handleLogin = async () => {
        setError("");

        if (!mail || !password) {
            setError(t("Please enter email and password"));
            return;
        }

        setLoading(true);
        try {
            await setPersistence(auth, browserLocalPersistence);
            const userCredential = await signInWithEmailAndPassword(auth, mail, password);
            await handleAuthResult(userCredential.user);
        } catch (err) {
            console.error("Login error:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async () => {
        setError("");

        if (!mail || !password) {
            setError(t("Please enter email and password"));
            return;
        }

        if (password.length < 6) {
            setError(t("Password must be at least 6 characters"));
            return;
        }

        setLoading(true);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, mail, password);
            const user = userCredential.user;

            const userRef = doc(fs, "users", user.uid);
            await setDoc(userRef, {
                mail,
                uid: user.uid,
                isNewUser: true,
                onboardingCompleted: false,
                createdAt: new Date().toISOString(),
            });

            localStorage.setItem("isNewUser", "true");
            localStorage.setItem("LOGstatus", "true");
            localStorage.setItem("USER_LOGIN", user.email);
            localStorage.setItem("USER_ID", user.uid);

            navigate("/onboarding");
        } catch (err) {
            console.error("Registration error:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = () => {
        if (authMode === "register") {
            handleRegister();
        } else {
            handleLogin();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSubmit();
        }
    };

    const toggleMode = () => {
        setAuthMode(authMode === "register" ? "login" : "register");
        setError("");
    };

    return (
        <div className="auth-modal-overlay">
            <div className="auth-modal-content">
                <button className="auth-modal-close-btn" onClick={onClose} type="button">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6L6 18" />
                        <path d="M6 6l12 12" />
                    </svg>
                </button>

                <div className="auth-modal-logo">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#ef4444" />
                        <circle cx="12" cy="9" r="2.5" fill="#ffffff" />
                        <circle cx="10" cy="7.5" r="1" fill="#ffffff" />
                        <circle cx="14" cy="7.5" r="1" fill="#ffffff" />
                    </svg>
                </div>

                <h1 className="auth-modal-title">{t("Let's get started")}</h1>
                <p className="auth-modal-subtitle">{t("Join FastDelivery.ge for the best food.")}</p>

                <button
                    className="auth-modal-google-btn"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    type="button"
                >
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32l3.55 2.76c2.07-1.91 3.29-4.73 3.29-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.76c-.99.66-2.23 1.06-3.73 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.11a6.8 6.8 0 010-4.22V7.05H2.18a11.01 11.01 0 000 9.9l3.66-2.84z" fill="#FBBC05" />
                        <path d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.09 14.97 0 12 0 7.7 0 3.99 2.47 2.18 6.05l3.66 2.84c.87-2.6 3.3-4.14 6.16-4.14z" fill="#EA4335" />
                    </svg>
                    {t("Continue with Google")}
                </button>

                <div className="auth-modal-divider">
                    <span>{t("or")}</span>
                </div>

                <div className="auth-modal-form">
                    <label className="auth-modal-label">{t("Email Address")}</label>
                    <div className="auth-modal-input-group">
                        <div className="auth-modal-input-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                                <path d="M3 7l9 6 9-6" />
                            </svg>
                        </div>
                        <input
                            type="email"
                            placeholder="name@example.com"
                            value={mail}
                            onChange={(e) => setMail(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                    </div>

                    <label className="auth-modal-label">{t("Password")}</label>
                    <div className="auth-modal-input-group">
                        <div className="auth-modal-input-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="5" y="11" width="14" height="10" rx="2" />
                                <path d="M12 3a4 4 0 014 4v4H8V7a4 4 0 014-4z" />
                            </svg>
                        </div>
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <button
                            className="auth-modal-eye-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            type="button"
                        >
                            {showPassword ? (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {error && <div className="auth-modal-error">{error}</div>}

                    <button
                        className="auth-modal-submit-btn"
                        onClick={handleSubmit}
                        disabled={loading}
                        type="button"
                    >
                        {authMode === "register" ? t("Registration") : t("Login")}
                    </button>

                    {authMode === "register" && (
                        <p className="auth-modal-terms">
                            {t("By registering, you agree to our")}{" "}
                            <a href="/terms">{t("Terms of Service")}</a>{" "}
                            {t("and")}{" "}
                            <a href="/privacy">{t("Privacy Policy")}</a>.
                        </p>
                    )}
                </div>
            </div>

            <div className="auth-modal-footer">
                <p>
                    {authMode === "register"
                        ? t("Already have an account?")
                        : t("Dont have an account?")}
                    <button
                        className="auth-modal-footer-link"
                        onClick={toggleMode}
                        type="button"
                    >
                        {authMode === "register" ? t("Log in") : t("Create an account")}
                    </button>
                </p>
            </div>
        </div>
    );
}

export default AuthModal;
